import React, { useState } from 'react';
import { useLocation } from 'wouter';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { getApiBaseUrl, joinUrl } from '../lib/queryClient';


const CreatePassword: React.FC = () => {
  const [location, setLocation] = useLocation();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const urlParams = new URLSearchParams(window.location.search);
  const email = urlParams.get('email');
  const token = urlParams.get('token');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    setIsLoading(true);

    try {
      const baseUrl = getApiBaseUrl();
      const createPasswordUrl = baseUrl ? joinUrl(baseUrl, '/auth/create-password') : '/auth/create-password';
      const response = await fetch(createPasswordUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, token, password }),
      });

      if (response.ok) {
        setLocation('/login?message=Password created successfully! Please log in.');
      } else {
        const data = await response.json();
        const errorMessage = data.message || 'Failed to create password';
        const errors = data.errors && Array.isArray(data.errors) ? data.errors : [];
        
        // Format errors as a list for better readability
        if (errors.length > 0) {
          setError(`${errorMessage}\n\n${errors.map((err: string) => `• ${err}`).join('\n')}`);
        } else {
          setError(errorMessage);
        }
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!email || !token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white p-4">
        <Card className="w-full max-w-md shadow-2xl border-0 bg-white/95">
          <CardHeader className="text-center space-y-2 pt-16">
            <CardTitle className="text-3xl font-bold text-servify-purple">Invalid Link</CardTitle>
            <CardDescription className="text-servify-purple/80">This password creation link is invalid or has expired.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <Button onClick={() => setLocation('/login')} className="w-full h-12 bg-servify-purple hover:bg-servify-purple-dark text-white font-semibold text-lg rounded-lg">
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-white p-4">
      <Card className="w-full max-w-md shadow-2xl border-0 bg-white/95">
        <CardHeader className="text-center space-y-2 pt-16">
          <CardTitle className="text-3xl font-bold text-servify-purple">Create Your Password</CardTitle>
          <CardDescription className="text-servify-purple/80">Welcome to Servify! Please create a password for your account.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label className="text-servify-purple font-medium">New Password</label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your new password"
                className="h-12 border-gray-300 focus:border-servify-purple focus:ring-servify-purple/20"
                required
              />
            </div>

            <div className="space-y-2">
              <label className="text-servify-purple font-medium">Confirm New Password</label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your new password"
                className="h-12 border-gray-300 focus:border-servify-purple focus:ring-servify-purple/20"
                required
              />
            </div>

            {error && (
              <div className="text-red-600 text-sm bg-red-50 p-3 rounded-lg whitespace-pre-line">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full h-12 bg-servify-purple hover:bg-servify-purple-dark text-white font-semibold text-lg rounded-lg disabled:opacity-50"
            >
              {isLoading ? 'Creating Password...' : 'Create Password'}
            </Button>
          </form>

          <div className="text-center">
            <p className="text-sm text-gray-600">
              Already have a password?{' '}
              <button onClick={() => setLocation('/login')} className="text-servify-purple hover:text-servify-purple-dark font-medium underline hover:no-underline">
                Log in here
              </button>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CreatePassword;
